import { useParams, Link, useLocation } from "react-router-dom";
import { useTrackOnce } from "@/lib/analytics/useTrackOnce";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlace } from "@/hooks/usePlace";
import { ArrowLeft } from "lucide-react";
import { Seo } from "@/components/common";
import { PlaceCorePublicView } from "@/components/placecore/public/PlaceCorePublicView";

const PlaceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const { place, loading, error } = usePlace(slug || "");

  // ── Analytics: place_viewed (once per place) ──
  useTrackOnce(
    "place_viewed",
    {
      place_id: place?.id ?? "",
      place_slug: place?.slug ?? null,
      href: location.pathname + location.search,
    },
    place?.id,
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-dvh flex flex-col bg-background">
        <SiteHeader density="compact" />
        <main className="flex-1 container py-8">
          <Skeleton className="h-5 w-24 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-3" />
          <Skeleton className="h-5 w-1/3 mb-8" />
          <div className="flex gap-3 mb-10">
            <Skeleton className="h-12 w-36 rounded-full" />
            <Skeleton className="h-12 w-28 rounded-full" />
          </div>
          <Skeleton className="h-48 w-full max-w-2xl rounded-xl mb-10" />
          <Skeleton className="h-24 w-full max-w-2xl" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  // 404 state
  if (!place) {
    return (
      <div className="min-h-dvh flex flex-col bg-background">
        <Seo title="Platsen hittades inte" noIndex />
        <SiteHeader density="compact" />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-2xl md:text-3xl mb-4 text-foreground">
            Platsen hittades inte
          </h1>
          <p className="text-muted-foreground mb-8">
            {error || "Vi kunde inte hitta platsen du letade efter."}
          </p>
          <Button asChild>
            <Link to="/explore">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till Utforska
            </Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return <PlaceCorePublicView place={place} />;
};

export default PlaceDetail;
